import React, { useEffect, useState } from 'react';
import axios from 'axios';
import '../App.css';

// Fetching data from ShipStation API using axios
const fetchOrderData = async (status) => {
    const API_KEY = process.env.REACT_APP_SHIPSTATION_DRMTLGY_API_KEY || '';
    const API_SECRET = process.env.REACT_APP_SHIPSTATION_DRMTLGY_API_SECRET || '';
    if (!API_KEY || !API_SECRET) return [];
    const authHeader = `Basic ${btoa(API_KEY + ":" + API_SECRET)}`;

    let allOrders = [];
    let page = 1;
    const pageSize = 100;

    try {
        while (true) {
            const response = await axios.get(
                `https://ssapi.shipstation.com/orders/listbytag?orderStatus=${status}&tagId=55064&page=${page}&pageSize=${pageSize}`,
                {
                    headers: {
                        "Authorization": authHeader,
                        'Content-Type': 'application/json',
                    }
                }
            );
            const orders = response.data.orders || [];
            allOrders = [...allOrders, ...orders];

            if (orders.length < pageSize) {
                break;
            }
            page++;
        }
        return allOrders;
    } catch (error) {
        console.error('Error fetching data from ShipStation:', error);
        return [];
    }
};

// Function for basic CSV export
const convertToCSV = (orders) => {
    if (orders.length === 0) return '';

    const headers = ['Order ID', 'Order Number', 'Order Status', 'Customer Name', 'Order Date', 'Order Total'];
    const csvRows = [headers.join(',')];

    orders.forEach(order => {
        const row = [
            `"${order.orderId || ''}"`,
            `"${order.orderNumber || ''}"`,
            `"${order.orderStatus || ''}"`,
            `"${order.customerName || ''}"`,
            `"${order.orderDate || ''}"`,
            `"${order.orderTotal || ''}"`
        ];
        csvRows.push(row.join(','));
    });

    return csvRows.join('\n');
};

// Function for detailed CSV export
const convertToDetailedCSV = (orders) => {
    if (orders.length === 0) return '';

    const headers = [
        'Order ID', 'Order Number', 'Order Status', 'Order Date', 'Customer Name',
        'Shipping Name', 'Shipping Address', 'Shipping City', 'Shipping State',
        'Shipping Postal Code', 'Shipping Country', 'Carrier Code', 'Service Code',
        'Item SKU', 'Item Name', 'Item Quantity', 'Item Unit Price', 'Item Total Price',
        'Item Weight', 'Item UPC', 'Item Warehouse Location', 'Order Total', 'Tag IDs',
        'Externally Fulfilled'
    ];

    const csvRows = [headers.join(',')];

    orders.forEach(order => {
        const shipTo = order.shipTo || {};
        const billTo = order.billTo || {};
        const items = order.items || [];
        const tagIds = (order.tagIds || []).join('; ');

        const shippingAddress = `${shipTo.street1 || ''} ${shipTo.street2 || ''}`.trim();

        if (items.length === 0) {
            const row = [
                `"${order.orderId || ''}"`,
                `"${order.orderNumber || ''}"`,
                `"${order.orderStatus || ''}"`,
                `"${order.orderDate || ''}"`,
                `"${billTo.name || shipTo.name || ''}"`,
                `"${shipTo.name || ''}"`,
                `"${shippingAddress}"`,
                `"${shipTo.city || ''}"`,
                `"${shipTo.state || ''}"`,
                `"${shipTo.postalCode || ''}"`,
                `"${shipTo.country || ''}"`,
                `"${order.carrierCode || ''}"`,
                `"${order.serviceCode || ''}"`,
                '""', '""', '""', '""', '""', '""', '""', '""',
                `"${order.orderTotal || ''}"`,
                `"${tagIds}"`,
                `"${order.externallyFulfilled || false}"`
            ];
            csvRows.push(row.join(','));
        } else {
            items.forEach(item => {
                const itemWeight = item.weight ? `${item.weight.value || ''} ${item.weight.units || ''}` : '';
                const itemTotalPrice = item.unitPrice && item.quantity ? (parseFloat(item.unitPrice) * parseInt(item.quantity)).toFixed(2) : '';

                const row = [
                    `"${order.orderId || ''}"`,
                    `"${order.orderNumber || ''}"`,
                    `"${order.orderStatus || ''}"`,
                    `"${order.orderDate || ''}"`,
                    `"${billTo.name || shipTo.name || ''}"`,
                    `"${shipTo.name || ''}"`,
                    `"${shippingAddress}"`,
                    `"${shipTo.city || ''}"`,
                    `"${shipTo.state || ''}"`,
                    `"${shipTo.postalCode || ''}"`,
                    `"${shipTo.country || ''}"`,
                    `"${order.carrierCode || ''}"`,
                    `"${order.serviceCode || ''}"`,
                    `"${item.sku || ''}"`,
                    `"${item.name || ''}"`,
                    `"${item.quantity || ''}"`,
                    `"${item.unitPrice || ''}"`,
                    `"${itemTotalPrice}"`,
                    `"${itemWeight}"`,
                    `"${item.upc || ''}"`,
                    `"${item.warehouseLocation || ''}"`,
                    `"${order.orderTotal || ''}"`,
                    `"${tagIds}"`,
                    `"${order.externallyFulfilled || false}"`
                ];
                csvRows.push(row.join(','));
            });
        }
    });

    return csvRows.join('\n');
};

// Function to download CSV
const downloadCSV = (csvContent, filename = 'orders.csv') => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

function ClientOrderStatus() {
    const [orders, setOrders] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [ordersPerPage] = useState(9);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [isLoading, setIsLoading] = useState(false);


    useEffect(() => {
        const loadOrders = async () => {
            if (!selectedStatus) return;
            setIsLoading(true);
            const data = await fetchOrderData(selectedStatus);
            setOrders(data);
            setCurrentPage(1);
            setIsLoading(false);
        };
        loadOrders();
    }, [selectedStatus]);

    const handleExportCSV = (detailed = false) => {
        const csvData = detailed ? convertToDetailedCSV(orders) : convertToCSV(orders);
        if (csvData) {
            const filename = detailed
                ? `drmtlgy_orders_detailed_${selectedStatus}_${new Date().toISOString().split('T')[0]}.csv`
                : `kitsch_orders_${selectedStatus}_${new Date().toISOString().split('T')[0]}.csv`;
            downloadCSV(csvData, filename);
        } else {
            alert('No data to export!');
        }
    };

    const indexOfLastOrder = currentPage * ordersPerPage;
    const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
    const currentOrders = orders.slice(indexOfFirstOrder, indexOfLastOrder);
    const totalPages = Math.ceil(orders.length / ordersPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const handleStatusChange = (e) => {
        setSelectedStatus(e.target.value);
    };

    return (
        <div className='clientOrderStatus'>
            <h1 className="text-gradient">Client Order Status</h1>
            <p className="subtitle">Track and manage your orders with ease</p>

            <div className='status-section mt-4'>
                <div className="status-header">
                    <i className="fas fa-search"></i>
                    <h3>Check Order Status</h3>
                </div>

                <div className='status-controls mt-3'>
                    <div className="select-wrapper">
                        <select
                            onChange={handleStatusChange}
                            value={selectedStatus}
                            className="status-select"
                        >
                            <option value="">Select Order Status</option>
                            <option value="awaiting_shipment">Awaiting Shipment</option>
                            <option value="shipped">Shipped</option>
                            <option value="on_hold">On Hold</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                        <i className="fas fa-chevron-down select-arrow"></i>
                    </div>

                    <button
                        className='fetch-btn'
                        onClick={() => setSelectedStatus(selectedStatus)}
                        disabled={!selectedStatus || isLoading}
                    >
                        <i className={`fas ${isLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i>
                        {isLoading ? 'Loading...' : 'Fetch Orders'}
                    </button>

                    {orders.length > 0 && (
                        <>
                            {/* <button
                                className='export-btn'
                                onClick={() => handleExportCSV(false)}
                                disabled={isLoading}
                            >
                                <i className="fas fa-file-export"></i>
                                Export Simple CSV ({orders.length})
                            </button> */}
                            <button
                                className='export-btn'
                                style={{ background: 'linear-gradient(90deg,#38b6ff,#38d996)', marginLeft: 8 }}
                                onClick={() => handleExportCSV(true)}
                                disabled={isLoading}
                            >
                                <i className="fas fa-file-export"></i>
                                Export Detailed CSV ({orders.length})
                            </button>
                        </>
                    )}
                </div>

                <div className='totalOrders mt-5'>
                    <h3>No. of Total Orders: {orders.length}</h3>
                    {isLoading && <p className="loading-text">Fetching all orders... This may take a moment.</p>}
                </div>

                <div className='container orderDetails d-flex gap-3 justify-content-center mt-5 flex-wrap'>
                    {isLoading ? (
                        <div className="loading-spinner" style={{ width: '100%', textAlign: 'center', padding: '2rem 0' }}>
                            <i className="fas fa-spinner fa-spin fa-2x"></i>
                            <p>Loading orders...</p>
                        </div>
                    ) : currentOrders.length > 0 ? (
                        currentOrders.map(order => (
                            <div key={order.orderId} className='card order-card'>
                                <div className="card-header">
                                    <h3>Order ID: {order.orderId}</h3>
                                </div>
                                <div className="card-body">
                                    <p><strong>Order Number:</strong> {order.orderNumber}</p>
                                    <p>
                                        <strong>Status:</strong>{' '}
                                        <span className={`status-badge status-${order.orderStatus}`}>
                                            {order.orderStatus}
                                        </span>
                                    </p>
                                    {order.customerName && <p><strong>Customer:</strong> {order.customerName}</p>}
                                    {order.orderDate && <p><strong>Order Date:</strong> {new Date(order.orderDate).toLocaleDateString()}</p>}
                                    {order.orderTotal && <p><strong>Total:</strong> ${parseFloat(order.orderTotal).toFixed(2)}</p>}
                                </div>
                            </div>
                        ))
                    ) : (
                        selectedStatus && !isLoading && <p className="no-orders">No orders found for the selected status.</p>
                    )}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="pagination d-flex justify-content-center align-items-center gap-3 mt-4">
                        <button
                            onClick={() => paginate(currentPage - 1)}
                            disabled={currentPage === 1}
                            className="pagination-btn"
                        >
                            <i className="fas fa-chevron-left"></i> Previous
                        </button>
                        <span className="page-info">
                            Page {currentPage} of {totalPages}
                        </span>
                        <button
                            onClick={() => paginate(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className="pagination-btn"
                        >
                            Next <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ClientOrderStatus;
